import { useEffect, useMemo, useState } from "react";
import { CalendarDays, Check, Menu, RefreshCw } from "lucide-react";
import { api, GcalEventItem, isTauriEnvironment } from "../lib/tauri";
import { useTaskStore } from "../stores/taskStore";
import { useUiStore } from "../stores/uiStore";
import { Button } from "./ui/Button";
import { Input } from "./ui/Input";
import { GlobalSearchButton } from "./GlobalSearch";

function dateKey(offsetDays = 0): string {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return d.toISOString().slice(0, 10);
}

function toJstIso(date: string, endOfDay = false): string {
  return `${date}T${endOfDay ? "23:59:59" : "00:00:00"}+09:00`;
}

function eventLabel(value: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString("ja-JP", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function CalendarView({ onMenuClick }: { onMenuClick?: () => void }) {
  const [startDate, setStartDate] = useState(dateKey());
  const [endDate, setEndDate] = useState(dateKey(14));
  const [events, setEvents] = useState<GcalEventItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const loadTasks = useTaskStore((s) => s.load);
  const showToast = useUiStore((s) => s.showToast);
  const setSettingsOpen = useUiStore((s) => s.setSettingsOpen);

  const range = useMemo(
    () => ({
      startIso: toJstIso(startDate),
      endIso: toJstIso(endDate, true),
    }),
    [startDate, endDate],
  );

  const loadEvents = async () => {
    if (!isTauriEnvironment()) return;
    setLoading(true);
    setMessage("");
    try {
      const rows = await api.gcalListEvents!(range.startIso, range.endIso);
      setEvents(rows);
      if (rows.length === 0) setMessage("予定はありません");
    } catch (e) {
      setMessage(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const importEvent = async (event: GcalEventItem) => {
    setMessage("");
    try {
      await api.gcalImportEvent!(event.id);
      showToast("予定をタスク化しました");
      await Promise.all([loadTasks(), loadEvents()]);
    } catch (e) {
      showToast(e instanceof Error ? e.message : "タスク化に失敗しました");
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex items-center gap-3 border-b border-border px-4 py-3 dark:border-border-dark">
        <button
          type="button"
          aria-label="メニュー"
          onClick={onMenuClick}
          className="flex h-9 w-9 items-center justify-center rounded-md text-muted hover:bg-hover dark:text-muted-dark dark:hover:bg-hover-dark md:hidden"
        >
          <Menu className="h-5 w-5" />
        </button>
        <h2 className="flex items-center gap-2 text-base font-bold">
          <CalendarDays className="h-4 w-4" /> カレンダー
        </h2>
        <GlobalSearchButton className="ml-auto" />
      </header>

      <div className="border-b border-border px-4 py-3 dark:border-border-dark">
        <div className="flex flex-col gap-2 md:flex-row md:items-end">
          <label className="min-w-0 flex-1 text-xs font-medium text-muted dark:text-muted-dark">
            開始日
            <Input
              className="mt-1"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </label>
          <label className="min-w-0 flex-1 text-xs font-medium text-muted dark:text-muted-dark">
            終了日
            <Input
              className="mt-1"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </label>
          <Button
            variant="primary"
            leftIcon={<RefreshCw className="h-4 w-4" />}
            disabled={loading}
            onClick={() => void loadEvents()}
          >
            {loading ? "読み込み中" : "更新"}
          </Button>
        </div>
      </div>

      {!isTauriEnvironment() ? (
        <div className="p-6 text-sm text-muted dark:text-muted-dark">
          Googleカレンダー連携はデスクトップ版で利用できます。
        </div>
      ) : message && events.length === 0 ? (
        <div className="p-6">
          <p className="mb-3 text-sm text-muted dark:text-muted-dark">{message}</p>
          <Button variant="outline" onClick={() => setSettingsOpen(true)}>
            設定を開く
          </Button>
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          <div className="space-y-2">
            {events.map((event) => (
              <div
                key={event.id}
                className="flex min-h-[64px] items-center gap-3 rounded-md border border-border bg-card px-4 py-3 dark:border-border-dark dark:bg-card-dark"
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold">{event.summary}</div>
                  <div className="mt-1 truncate text-xs text-muted dark:text-muted-dark">
                    {eventLabel(event.start)} - {eventLabel(event.end)}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant={event.linked ? "outline" : "primary"}
                  disabled={event.linked}
                  leftIcon={event.linked ? <Check className="h-3.5 w-3.5" /> : undefined}
                  onClick={() => void importEvent(event)}
                >
                  {event.linked ? "取込済" : "タスク化"}
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
